/**
 * `/c/:symbol` — one company, tabbed (§10.3).
 *
 * The tab bar is built from the counts endpoint. A tab with zero rows is not
 * rendered, so today's cement companies show six tabs and a
 * board-report-rich company later will show nine, with no code change. A
 * hardcoded bar mostly showing "No data" is the failure mode to avoid.
 */
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { Link, useLocation, useParams, useSearch } from 'wouter'

import type { CompanyDetail } from '@server/sql/companies.ts'
import type { GapsResponse } from '@server/sql/gaps.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count, percent0, timestamp } from '~/i18n/format.ts'
import { Bar, Skeleton, bandOf } from '~/components/ui/primitives.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Tabs, type TabDef } from '~/components/ui/tabs.tsx'
import { Raw } from '~/components/ui/raw.tsx'
import { CompanyOverview } from '~/features/companies/overview.tsx'
import { CompanyDocuments } from '~/features/companies/documents.tsx'
import { CompanyFinancials } from '~/features/statements/financials.tsx'
import { CompanyNotes } from '~/features/companies/notes.tsx'
import { CompanyAuditor } from '~/features/companies/auditor.tsx'
import { CompanyQuality } from '~/features/quality/company-quality.tsx'
import { GapsPanel } from '~/features/gaps/gaps-panel.tsx'
import { CompanyAnnouncements } from '~/features/announcements/panel.tsx'
import { CompanyCrosscheck } from '~/features/announcements/crosscheck.tsx'
import { useDocumentTitle } from '~/lib/title.ts'
import {
  CompanyGovernance, CompanyRemuneration, CompanySubsidiaries,
} from '~/features/companies/governance.tsx'
import { cn } from '~/lib/cn.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'

const TAB_IDS = [
  'overview', 'financials', 'notes', 'auditor', 'documents', 'quality', 'gaps',
  'governance', 'remuneration', 'subsidiaries', 'announcements', 'crosscheck',
] as const
export type TabId = (typeof TAB_IDS)[number]

export function CompanyScreen() {
  const { symbol = '' } = useParams<{ symbol: string }>()
  const { t, tn, locale, v } = useT()
  const superadmin = useSuperadmin()
  const search = useSearch()
  const [location, navigate] = useLocation()

  const { data, error, refetch } = useQuery({
    queryKey: keys.company(symbol),
    queryFn: () => api<CompanyDetail>(`/api/companies/${encodeURIComponent(symbol)}`),
  })

  // The symbol alone until the name arrives, so the tab identifies itself
  // immediately rather than sitting on the generic title through the fetch.
  useDocumentTitle(data ? `${data.symbol} ${data.nameEn ?? ''}`.trim() : symbol)

  /**
   * The gap summary, for the tab's own count.
   *
   * A separate request rather than a field on the company response: it joins
   * capture's 25,000-row file index, and the company header is on the
   * critical path of every company page while this is not. It loads
   * alongside and the tab appears when it arrives.
   *
   * The badge is `documentsIncomplete` -- never read plus stalled -- because
   * both halves are documents. Badging the sum of documents, review items and
   * unmapped facts would put "4,343" on a tab and mean nothing by it.
   */
  const { data: gaps } = useQuery({
    queryKey: keys.gaps(symbol),
    queryFn: () => api<GapsResponse>(`/api/gaps/${encodeURIComponent(symbol)}`),
    staleTime: 30_000,
    enabled: Boolean(symbol) && superadmin,
  })

  // The open tab is a URL parameter: a reload, a bookmark and a link all
  // land where they were sent (§10).
  const raw = new URLSearchParams(search).get('tab')
  const setTab = useCallback(
    (id: TabId) => {
      const sp = new URLSearchParams(search)
      if (id === 'overview') sp.delete('tab')
      else sp.set('tab', id)
      const s = sp.toString()
      navigate(`${location}${s ? `?${s}` : ''}`, { replace: true })
    },
    [search, location, navigate],
  )

  if (error) {
    const e = error as ApiError
    return (
      <Column>
        {e.status === 404 ? (
          <EmptyState title={e.message}>
            <Link href="/results" className="link-inline">
              {t('nav.results')}
            </Link>
          </EmptyState>
        ) : (
          <ErrorState
            title={t('common.error')}
            detail={e.message}
            requestId={e.requestId}
            onRetry={() => void refetch()}
            retryLabel={t('action.retry')}
          />
        )}
      </Column>
    )
  }

  if (!data) {
    return (
      <Column>
        <Skeleton className="h-8 w-72" />
        <Skeleton className="mt-2 h-4 w-96" />
        <Skeleton className="mt-6 h-96 w-full" />
      </Column>
    )
  }

  const c = data.counts
  const n = (x: number) => count(x, locale)
  // Built from counts, and only what has rows.
  const tabs: TabDef<TabId>[] = [
    { id: 'overview', label: t('company.tab.overview') },
    c.facts > 0 && { id: 'financials' as const, label: t('company.tab.financials'), count: n(c.facts) },
    c.notes > 0 && { id: 'notes' as const, label: t('company.tab.notes'), count: n(c.notes) },
    c.auditors > 0 && { id: 'auditor' as const, label: t('company.tab.auditor'), count: n(c.auditors) },
    { id: 'documents' as const, label: t('company.tab.documents'), count: n(data.documents.total) },
    superadmin && c.checks > 0 && { id: 'quality' as const, label: t('company.tab.quality'), count: n(c.checks) },
    // Always offered once the answer is in, even at zero: "nothing is
    // missing" is a finding, and a tab that vanishes when a company is
    // complete makes that finding unreachable.
    superadmin && gaps && { id: 'gaps' as const, label: t('gaps.title'),
              count: gaps.summary.documentsIncomplete > 0
                ? n(gaps.summary.documentsIncomplete) : undefined },
    c.people > 0 && { id: 'governance' as const, label: t('company.tab.governance'), count: n(c.people) },
    c.remuneration > 0 && { id: 'remuneration' as const, label: t('company.tab.remuneration'), count: n(c.remuneration) },
    c.subsidiaries > 0 && { id: 'subsidiaries' as const, label: t('company.tab.subsidiaries'), count: n(c.subsidiaries) },
    // Announcements are capture's, so this tab appears for a company the
    // parser has not reached yet -- which is the point: it is the side of
    // the comparison that does not depend on extraction having worked.
    c.announcements > 0 && { id: 'announcements' as const, label: t('company.tab.announcements'), count: n(c.announcements) },
    // Badged with the disagreements, not the comparison count: 1,448 checks
    // that mostly passed is not the number anyone is looking for.
    c.crosscheck > 0 && { id: 'crosscheck' as const, label: t('company.tab.crosscheck'),
      count: c.crosscheckFailed > 0 ? n(c.crosscheckFailed) : undefined },
  ].filter(Boolean) as TabDef<TabId>[]

  // A tab named in the URL that no longer exists falls back rather than
  // rendering nothing -- the corpus grows and shrinks under saved links.
  //
  // This is also what stops `?tab=quality` in a bookmark reaching a panel the
  // tab bar is deliberately not offering: the tab list is already filtered by
  // the flag, so a name that is not in it simply is not a tab.
  const tab: TabId = tabs.some((x) => x.id === raw) ? (raw as TabId) : 'overview'
  const band = bandOf(data.quality.passRate)

  return (
    <Column>
      {/* --- header --------------------------------------------------- */}
      <header className="pb-3">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="tnum font-[family-name:var(--font-mono)] text-[length:var(--text-data)] text-[var(--text-muted)]">
            {data.symbol}
          </span>
          <h1 className="text-[length:var(--text-display)] font-semibold tracking-tight">
            {data.nameEn ?? data.symbol}
          </h1>
          <Raw lang="ar" className="text-[length:var(--text-title)] text-[var(--text-muted)]">
            {data.entityNameAr ?? data.nameAr ?? ''}
          </Raw>
        </div>

        <div className="tnum mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[length:var(--text-data)] text-[var(--text-muted)]">
          <span>{data.sector.en}</span>
          <span className="text-[var(--border-strong)]">›</span>
          <span>{data.industry && (v('industry', data.industry) ?? data.industry)}</span>
          <Sep />
          <span>
            {tn('company.parsedDocuments', data.documents.total, {
              n: count(data.documents.total, locale),
            })}
          </span>
          {data.documents.years && (
            <>
              <Sep />
              <span>{data.documents.years[0]}–{data.documents.years[1]}</span>
            </>
          )}
          {data.currency && (
            <>
              <Sep />
              <span>{data.currency}</span>
            </>
          )}
          {superadmin && (
            <>
              <Sep />
              <Bar value={data.quality.passRate} />
              <span
                className={cn(
                  band === 'pass' ? 'text-[var(--pass)]'
                    : band === 'warn' ? 'text-[var(--warn)]'
                    : band === 'fail' ? 'text-[var(--fail)]'
                    : 'text-[var(--text-faint)]',
                )}
              >
                {t('quality.passRateOf', {
                  pct: percent0(data.quality.passRate, locale),
                  passed: count(data.quality.passed, locale),
                  total: count(data.quality.checks, locale),
                })}
              </span>
            </>
          )}
          {data.lastParsedAt && (
            <>
              <Sep />
              <span className="text-[var(--text-faint)]" title={t('company.lastParsed')}>
                {timestamp(data.lastParsedAt, locale)}
              </span>
            </>
          )}
        </div>
      </header>

      <Tabs tabs={tabs} value={tab} onChange={setTab} />

      <div className="pt-5">
        {tab === 'overview' && <CompanyOverview company={data} />}
        {tab === 'financials' && <CompanyFinancials symbol={symbol} />}
        {tab === 'notes' && <CompanyNotes symbol={symbol} />}
        {tab === 'auditor' && <CompanyAuditor symbol={symbol} />}
        {tab === 'documents' && <CompanyDocuments symbol={symbol} />}
        {tab === 'quality' && <CompanyQuality symbol={symbol} />}
        {tab === 'gaps' && <GapsPanel symbol={symbol} />}
        {tab === 'governance' && <CompanyGovernance symbol={symbol} />}
        {tab === 'remuneration' && <CompanyRemuneration symbol={symbol} />}
        {tab === 'subsidiaries' && <CompanySubsidiaries symbol={symbol} />}
        {tab === 'announcements' && <CompanyAnnouncements symbol={symbol} />}
        {tab === 'crosscheck' && <CompanyCrosscheck symbol={symbol} />}
      </div>
    </Column>
  )
}

const Sep = () => <span className="text-[var(--border-strong)]">·</span>

function Column({ children }: { children: React.ReactNode }) {
  return <div className="mx-auto w-full max-w-[84rem] px-4 pb-10 pt-5 sm:px-5 2xl:max-w-[110rem]">{children}</div>
}
